/**
 * Utilities for detecting and responding to Cloudflare challenges.
 *
 * Unlike the previous Imperva flow, Cloudflare challenges should not be
 * injected into the DOM from an Ajax response. In React apps, the caller
 * should instead decide whether to redirect, reload, or show a blocking UI.
 */

export const challengeHeaderName = 'cf-mitigated';
export const challengeContainerId = 'cloudflare-challenge';
const challengePathPrefix = '/cdn-cgi/challenge-platform/';

const getHeader = (headers, name) => {
    if (!headers) {
        return;
    }

    const match = Object.keys(headers).find(
        (headerName) => headerName.toLowerCase() === name.toLowerCase(),
    );

    return match ? headers[match] ?? undefined : undefined;
};

/**
 * Check whether the response matches common Cloudflare challenge signals.
 */
export const isChallengeRequest = (response) => {
    const mitigatedHeader = getHeader(response?.headers, challengeHeaderName);

    return (
        mitigatedHeader?.toLowerCase() === 'challenge'
    );
};

/**
 * Render Cloudflare challenge HTML in a fullscreen modal container.
 */
export const renderChallengeResponse = (response) => {
    if (!response?.data || typeof response.data !== 'string') {
        console.error('No challenge response to render.');
        return;
    }
    //We need to "reload" the page so the challenge can be displayed, since the challenge is not meant to be rendered in an ajax response.
    //We can't do window.location.reload() because that causes the page to POST to the same url.
    //We can't render HTML because CF then redirects us to the original challenged page which was the ajax call.
    window.location.replace(window.location.href);
};

/**
 * Analyze a response and render challenge markup when needed.
 */
export const analyzeResponse = (response) => {
    if (!response || isChallengeRequest(response)) {
        renderChallengeResponse(response);
        return;
    }

    return response;
};

/**
 * Parses script tags from a given container element and optionally reprocesses them using a callback.
 *
 * @param {HTMLElement} container - The container element from which to parse script tags.
 * @param {ReprocessTagsCallback} [callback] - An optional callback function to reprocess the script tags.
 * @returns {Promise<void>}
 */
export const parseScriptTagsAsync = async (container, callback) => {
    const sortedScripts = getSortedScriptsByPriority(container);

    const priorityScripts = sortedScripts.filter((script) => script.hasAttribute('priority'));

    const unsortedScripts = sortedScripts.filter((script) => !script.hasAttribute('priority'));

    for (const script of priorityScripts) {
        await new Promise((resolve) => {
            cloneScriptTag(script, callback, resolve);
        });
    }

    await Promise.all(
        unsortedScripts.map(
            (oldScriptElement) =>
                new Promise((resolve) => {
                    cloneScriptTag(oldScriptElement, callback, resolve);
                }),
        ),
    );
};

const cloneScriptTag = (scriptElement, callback, onload) => {
    if (scriptElement.innerHTML?.includes('document.write')) {
        console.error("Error: Unable to reprocess this script tag as it contains a 'document.write()' call.", scriptElement);
        if (typeof onload === 'function') {
            onload();
        }
    }
    else {
        try {
            const newScriptElement = document.createElement('script');

            Array.from(scriptElement.attributes).forEach((attribute) => {
                newScriptElement.setAttribute(attribute.name, attribute.value);
            });

            const scriptText = document.createTextNode(scriptElement.innerHTML);
            newScriptElement.appendChild(scriptText);

            if (typeof onload === 'function') {
                const { src, type } = newScriptElement;

                if (
                    src &&
                    type !== 'text/plain' &&
                    (src.startsWith('http://') || src.startsWith('https://') || src.startsWith('//'))
                ) {
                    newScriptElement.onload = () => onload();

                    newScriptElement.onerror = () => {
                        console.error(`Failed to load script: ${newScriptElement.src}`);
                        onload();
                    };
                }
                else {
                    setTimeout(onload, 0);
                }
            }

            if (scriptElement.parentNode) {
                scriptElement.parentNode.replaceChild(newScriptElement, scriptElement);
            }
            else {
                document.body.replaceChild(newScriptElement, scriptElement);
            }

            if (typeof callback === 'function') {
                callback(scriptElement, newScriptElement);
            }
        }
        catch (error) {
            console.error('Error: Unable to reprocess this script tag.', error, scriptElement);
            if (typeof onload === 'function') {
                onload();
            }
        }
    }
};

/**
 * Retrieves all script tags from a given container element and sorts them based on their 'priority' attribute.
 *
 * @param {HTMLElement} container - The container element from which to retrieve script tags.
 * @returns {HTMLScriptElement[]} An array of script elements sorted by priority.
 */
const getSortedScriptsByPriority = (container) => {
    const scripts = Array.from(container.querySelectorAll('script'));

    const sortedScripts = scripts.sort((a, b) => {
        const aPriority = Number(a.getAttribute('priority') ?? 'Infinity');
        const bPriority = Number(b.getAttribute('priority') ?? 'Infinity');

        return aPriority - bPriority;
    });

    return sortedScripts;
};

export default {
    parseScriptTagsAsync,
    isChallengeRequest,
    analyzeResponse,
};